// ---------------------------------------------------------------------------
// What one material on the Stock tab's list says when it is not being edited
// (contract v14): what it books, and one status line — where it stands, in a
// tone its dot wears. Pure; `StockCartItem` renders it.
// ---------------------------------------------------------------------------

import type { TFunction } from 'i18next';
import type { ITaskStockClaim } from '@interfaces/IScheduler';
import { estimatedOnHand } from '@d-orca/shared/client/logistics';
import { formatDay } from '../../LogisticsPage/logistics.format';
import { needOf, type ClaimOutcome } from './claimEvaluation';
import type { ICheckoutPlan } from './checkoutPlan';
import { bookingCovers, type IOwnBooking } from './joinOrder';
import { floorsLabel } from './takeoffByFloor';

/** The reads a row can fold on. A blank row and one over its CAD ceiling
 *  block Save, so they always keep their form. */
export type FoldedOutcome = Exclude<ClaimOutcome, { kind: 'idle' } | { kind: 'over' }>;

export function isFoldable(outcome: ClaimOutcome): outcome is FoldedOutcome {
  return outcome.kind !== 'idle' && outcome.kind !== 'over';
}

export type CartTone = 'ok' | 'info' | 'warn' | 'err';

export interface ICartStatus {
  tone: CartTone;
  text: string;
  /** The order the line jumps to, when it names this task's own. */
  jumpRef: string | null;
}

export interface ICartStatusInput {
  claim: ITaskStockClaim;
  outcome: FoldedOutcome;
  /** This task's booking on an open order for the material. */
  booking: IOwnBooking | null;
  plan: ICheckoutPlan;
  lang: string;
  t: TFunction;
}

const KEY = 'scheduler.panel.stock.cart';

export function cartStatus({ claim, outcome, booking, plan, lang, t }: ICartStatusInput): ICartStatus {
  const say = (tone: CartTone, text: string): ICartStatus => ({ tone, text, jumpRef: null });
  if (outcome.kind === 'ok') {
    return say('ok', t(`${KEY}.stInStock`, { amount: estimatedOnHand(outcome.level), unit: claim.unit }));
  }
  if (outcome.kind === 'unit-mismatch') return say('warn', t(`${KEY}.stUnit`));
  // A shortfall: none on site, or part of it.
  if (booking !== null && bookingCovers(booking, needOf(outcome))) {
    const { order } = booking;
    const text =
      order.dueDate === null
        ? t(`${KEY}.stOnOrderNoEta`, { ref: order.ref })
        : t(`${KEY}.stOnOrder`, { ref: order.ref, date: formatDay(order.dueDate, lang) });
    return { tone: 'ok', text, jumpRef: order.ref };
  }
  const join = plan.joins.find((j) => j.claim.id === claim.id);
  if (join) return say('info', t(`${KEY}.stOnItsWay`, { ref: join.order.ref }));
  const blocked = plan.blocked.find((b) => b.claim.id === claim.id);
  if (blocked) return say('warn', t(`${KEY}.stBlocked`, { ref: blocked.order.ref }));
  return outcome.kind === 'partial'
    ? say('warn', t(`${KEY}.stShort`, { short: outcome.short, unit: claim.unit }))
    : say('err', t(`${KEY}.stNone`));
}

/**
 * The floors a booking's elements were picked FROM, by name, in first-seen
 * order — not the task's whole scope, which named every floor the task covers
 * for one slab on one floor (ruling 88, 2026-09-14: "only attach the floor/s
 * the material/s were selected from"). A claim that recorded no picked floor
 * (free text, or stored before elements carried their floors) falls back to
 * the scope; so does one whose floors the site catalog no longer knows.
 */
export function pickedFloorNames(
  claim: ITaskStockClaim,
  nameOf: (floorId: string) => string | undefined,
  scope: string[],
): string[] {
  const ids = [...new Set((claim.elements ?? []).flatMap((e) => e.floorIds))];
  const names = ids.flatMap((id) => {
    const name = nameOf(id);
    return name === undefined ? [] : [name];
  });
  return names.length > 0 ? names : scope;
}

/** "1 Slab · 12 Wall · 4F" — each element type the claim books, in the tab's
 *  own `chosenKind` words, then the task's floors as the scope line names
 *  them. A free-text claim books no elements: its floors alone. */
export function bookedLabel(claim: ITaskStockClaim, floorNames: string[], t: TFunction): string {
  const kinds = (claim.elements ?? [])
    .filter((entry) => entry.globalIds.length > 0)
    .map((entry) => t('scheduler.panel.stock.chosenKind', { count: entry.globalIds.length, kind: entry.kind }));
  return [...kinds, floorsLabel(floorNames, t)].join(' · ');
}
